import React from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Checkbox,
  FormControlLabel,
  FormGroup,
  MenuItem,
  Typography,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
  GASTROSTOMY_OPTIONS,
  INFUSION_OPTIONS,
  OXYGEN_OPTIONS,
  type MedicalDevices,
} from '../../../constants/patientMedicalDevices';
import IntakeFormRow from '../IntakeFormRow';
import IntakeTextField from '../IntakeTextField';

type Props = {
  value: MedicalDevices;
  onChange: (v: MedicalDevices) => void;
};

const MedicalDevicesFields: React.FC<Props> = ({ value, onChange }) => {
  const toggleMulti = (key: 'oxygen' | 'infusion', option: string) => {
    const current = value[key] ?? [];
    const next = current.includes(option)
      ? current.filter((x) => x !== option)
      : [...current, option];
    onChange({ ...value, [key]: next });
  };

  const setText = (key: keyof MedicalDevices, text: string) => {
    onChange({ ...value, [key]: text || undefined });
  };

  return (
    <Accordion
      disableGutters
      elevation={0}
      sx={{ border: 1, borderColor: 'divider', '&:before': { display: 'none' } }}
    >
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <Typography variant="subtitle2">医療機器（詳細）</Typography>
      </AccordionSummary>
      <AccordionDetails sx={{ bgcolor: 'background.paper', pt: 0.5 }}>
        <IntakeFormRow label="酸素" align="flex-start">
          <FormGroup row>
            {OXYGEN_OPTIONS.map((opt) => (
              <FormControlLabel
                key={opt}
                control={
                  <Checkbox
                    size="small"
                    checked={(value.oxygen ?? []).includes(opt)}
                    onChange={() => toggleMulti('oxygen', opt)}
                  />
                }
                label={opt}
              />
            ))}
          </FormGroup>
        </IntakeFormRow>

        <IntakeFormRow label="酸素（詳細）">
          <IntakeTextField
            placeholder="例: 2L、使用時間など"
            value={value.oxygen_note ?? ''}
            onChange={(e) => setText('oxygen_note', e.target.value)}
          />
        </IntakeFormRow>

        <IntakeFormRow label="インスリン">
          <IntakeTextField
            placeholder="薬剤"
            value={value.insulin ?? ''}
            onChange={(e) => setText('insulin', e.target.value)}
          />
        </IntakeFormRow>

        <IntakeFormRow label="血糖測定">
          <IntakeTextField
            placeholder="例: 3回/日"
            value={value.blood_glucose_per_day ?? ''}
            onChange={(e) => setText('blood_glucose_per_day', e.target.value)}
          />
        </IntakeFormRow>

        <IntakeFormRow label="点滴" align="flex-start">
          <FormGroup row>
            {INFUSION_OPTIONS.map((opt) => (
              <FormControlLabel
                key={opt}
                control={
                  <Checkbox
                    size="small"
                    checked={(value.infusion ?? []).includes(opt)}
                    onChange={() => toggleMulti('infusion', opt)}
                  />
                }
                label={opt}
              />
            ))}
          </FormGroup>
        </IntakeFormRow>

        <IntakeFormRow label="整形注射">
          <IntakeTextField
            placeholder="薬剤"
            value={value.orthopedic_injection ?? ''}
            onChange={(e) => setText('orthopedic_injection', e.target.value)}
          />
        </IntakeFormRow>

        <IntakeFormRow label="胃瘻">
          <IntakeTextField
            select
            value={value.gastrostomy ?? ''}
            onChange={(e) => onChange({ ...value, gastrostomy: e.target.value || undefined })}
          >
            <MenuItem value="">未選択</MenuItem>
            {GASTROSTOMY_OPTIONS.map((o) => (
              <MenuItem key={o} value={o}>
                {o}
              </MenuItem>
            ))}
          </IntakeTextField>
        </IntakeFormRow>

        <IntakeFormRow label="腸瘻">
          <IntakeTextField
            placeholder="メーカー"
            value={value.enterostomy ?? ''}
            onChange={(e) => setText('enterostomy', e.target.value)}
          />
        </IntakeFormRow>

        <IntakeFormRow label="気切チューブ">
          <IntakeTextField
            placeholder="メーカー"
            value={value.tracheostomy_tube ?? ''}
            onChange={(e) => setText('tracheostomy_tube', e.target.value)}
          />
        </IntakeFormRow>

        <IntakeFormRow label="ストマ">
          <IntakeTextField
            placeholder="部位とメーカー"
            value={value.stoma ?? ''}
            onChange={(e) => setText('stoma', e.target.value)}
          />
        </IntakeFormRow>

      </AccordionDetails>
    </Accordion>
  );
};

export default MedicalDevicesFields;
